import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { AddChannelModal } from './AddChannelModal';

const configOptions = [
  {
    icon: "📋",
    title: "Edit Media Plan",
    description: "Review questionnaire, objectives and campaign requirements",
    link: "#"
  },
  {
    icon: "🤝",
    title: "View Deals",
    description: "View and pre-select deals to ensure they are included in your recommendations",
    link: "/partners/display/deals"
  },
  {
    icon: "👥",
    title: "Partner Selection",
    description: "Pre-select partners to ensure they are included in your recommendations",
    link: "#"
  }
];

const mockAIRecommendations = [
  {
    id: 'social_reactions',
    name: 'Social Media - Reactions',
    scores: {
      notice: 85,
      want: 70,
      seek: 60,
      try: 75,
      talk: 90,
      recommend: 80
    },
    overallScore: 82,
    reasoning: "High effectiveness for brand awareness and engagement. Active Meta partnership deal expiring soon.",
    isAIRecommended: true
  },
  {
    id: 'display_qr',
    name: 'Display - QR Code',
    scores: {
      notice: 75,
      want: 85,
      seek: 80,
      try: 65,
      talk: 70,
      recommend: 85
    },
    overallScore: 77,
    reasoning: "Strong performance in driving user engagement. Matches target audience behavior patterns.",
    isAIRecommended: true
  },
  // Add more channels with similar structure...
];

const availableChannels = [
  { 
    id: 'social_reactions', 
    name: 'Social Media - Reactions',
    scores: {
      notice: 70,
      want: 65,
      seek: 75,
      try: 60,
      talk: 80,
      recommend: 70
    },
    overallScore: 75
  },
  { 
    id: 'display_qr', 
    name: 'Display - QR Code',
    scores: {
      notice: 75,
      want: 70,
      seek: 65,
      try: 80,
      talk: 60,
      recommend: 75
    },
    overallScore: 72
  },
  { id: 'wom_reviews', name: 'WOM - Reviews', scores: { notice: 65, want: 75, seek: 70, try: 75, talk: 85, recommend: 80 }, overallScore: 78 },
  { id: 'print_editorial', name: 'Print - Advertorial or Editorial', scores: { notice: 80, want: 70, seek: 65, try: 60, talk: 70, recommend: 65 }, overallScore: 70 },
  { id: 'wom_expert', name: 'WOM - Expert', scores: { notice: 70, want: 80, seek: 75, try: 65, talk: 90, recommend: 85 }, overallScore: 78 },
  { id: 'display_web', name: 'Display - Company Web or Social Site', scores: { notice: 75, want: 70, seek: 75, try: 60, talk: 80, recommend: 70 }, overallScore: 74 },
  { id: 'wom_family', name: 'WOM - Friends or Family', scores: { notice: 60, want: 70, seek: 65, try: 75, talk: 85, recommend: 75 }, overallScore: 72 },
  { id: 'search_sponsored', name: 'Search - Sponsored Results', scores: { notice: 80, want: 75, seek: 70, try: 65, talk: 85, recommend: 80 }, overallScore: 76 },
  { id: 'direct_email', name: 'Direct - Email', scores: { notice: 70, want: 65, seek: 75, try: 60, talk: 80, recommend: 70 }, overallScore: 70 },
  { id: 'social_instagram', name: 'Social Media - Instagram', scores: { notice: 75, want: 70, seek: 75, try: 60, talk: 85, recommend: 75 }, overallScore: 74 },
  { id: 'video_youtube', name: 'Video - YouTube', scores: { notice: 70, want: 65, seek: 75, try: 60, talk: 80, recommend: 70 }, overallScore: 72 },
  { id: 'social_facebook', name: 'Social Media - Facebook', scores: { notice: 75, want: 70, seek: 75, try: 60, talk: 85, recommend: 75 }, overallScore: 74 },
  { id: 'display_banner', name: 'Display - Banner Ads', scores: { notice: 70, want: 65, seek: 75, try: 60, talk: 80, recommend: 70 }, overallScore: 72 },
  { id: 'email_newsletter', name: 'Email - Newsletter', scores: { notice: 60, want: 55, seek: 65, try: 50, talk: 70, recommend: 60 }, overallScore: 62 },
  { id: 'search_organic', name: 'Search - Organic Results', scores: { notice: 70, want: 65, seek: 75, try: 60, talk: 80, recommend: 70 }, overallScore: 72 }
];

export function SelectChannelsPage() {
  const { id } = useParams();
  const [scenario, setScenario] = useState(null);
  const [showChannelModal, setShowChannelModal] = useState(false);
  const [selectedChannels, setSelectedChannels] = useState([]);
  const [recommendedChannels, setRecommendedChannels] = useState([]);
  const [showAIRecommendations, setShowAIRecommendations] = useState(false);
  const [allChannelsSelected, setAllChannelsSelected] = useState(false);
  const [allAIChannelsSelected, setAllAIChannelsSelected] = useState(false);
  const [allChannels, setAllChannels] = useState([]);

  // Load scenario data
  useEffect(() => {
    const storedScenarios = JSON.parse(localStorage.getItem('channelScenarios') || '[]');
    const currentScenario = storedScenarios.find(s => s.id.toString() === id);
    setScenario(currentScenario);

    // Load selected channels if any
    const storedChannels = JSON.parse(localStorage.getItem(`channelSelections_${id}`) || '[]');
    setSelectedChannels(storedChannels);
  }, [id]);

  useEffect(() => {
    setAllChannelsSelected(selectedChannels.length === recommendedChannels.length);
    
    const aiRecommendedIds = recommendedChannels
      .filter(c => c.isAIRecommended)
      .map(c => c.id);
    
    setAllAIChannelsSelected(
      aiRecommendedIds.length > 0 && 
      aiRecommendedIds.every(id => selectedChannels.includes(id))
    );
  }, [selectedChannels, recommendedChannels]);

  useEffect(() => {
    if (showAIRecommendations) {
      setAllChannels(prev => {
        const existingIds = prev.map(c => c.id);
        const newChannels = recommendedChannels.filter(c => !existingIds.includes(c.id));
        return [...prev, ...newChannels];
      });
    }
  }, [showAIRecommendations, recommendedChannels]);

  const handleGetAIRecommendations = () => {
    setShowAIRecommendations(true);
    setRecommendedChannels(mockAIRecommendations);
  };

  const handleChannelSelect = (channelId) => {
    const updatedSelection = selectedChannels.includes(channelId)
      ? selectedChannels.filter(id => id !== channelId)
      : [...selectedChannels, channelId];
    
    setSelectedChannels(updatedSelection);
    localStorage.setItem(`channelSelections_${id}`, JSON.stringify(updatedSelection));
  };

  const handleDeleteChannel = (channelId) => {
    setAllChannels(prev => prev.filter(c => c.id !== channelId));
    setSelectedChannels(prev => prev.filter(id => id !== channelId));
    localStorage.setItem(
      `channelSelections_${id}`, 
      JSON.stringify(selectedChannels.filter(id => id !== channelId))
    );
  };

  return (
    <main className="main-content">
      {scenario && (
        <>
          <div className="project-details-container">
            <div className="project-details">
              <h2 className="details-title">Scenario: {scenario.name}</h2>
              <div className="summary-grid">
                <div className="summary-item">
                  <span className="summary-label">Client</span>
                  <span className="summary-value compact">Amazon Inc</span>
                </div>
                <div className="summary-item">
                  <span className="summary-label">Brand</span>
                  <span className="summary-value compact">Prime Video</span>
                </div>
                <div className="summary-item">
                  <span className="summary-label">Date Range</span>
                  <span className="summary-value compact">{scenario.dateRange}</span>
                </div>
                <div className="summary-item">
                  <span className="summary-label">Budget</span>
                  <span className="summary-value compact">{scenario.budget}</span>
                </div>
              </div>
            </div>

            <div className="audience-details">
              <h2 className="details-title">Audience</h2>
              <div className="summary-grid">
                <div className="summary-item">
                  <span className="summary-label">Connection Survey</span>
                  <span className="summary-value compact">Demo Survey</span>
                </div>
                <div className="summary-item">
                  <span className="summary-label">Audience 1</span>
                  <span className="summary-value compact">Early adopters 35-44</span>
                </div>
              </div>
            </div>
          </div>

          {/* Configuration Options */}
          <h2 className="section-title">Configuration Options</h2>
          <div className="config-options-grid">
            {configOptions.map((option, index) => (
              <Link 
                key={index}
                to={option.link}
                className="config-option-card"
              >
                <div className="config-option-icon">{option.icon}</div>
                <div className="config-option-content">
                  <div className="config-option-title">{option.title}</div>
                  <div className="config-option-description">{option.description}</div>
                </div>
              </Link>
            ))}
          </div>

          {/* Channel Selection */}
          <div className="channel-selection-section" style={{ marginTop: '2rem' }}>
            <div className="section-header">
              <h2 className="section-title">Channel Selection</h2>
              <div style={{ display: 'flex', gap: '1rem' }}>
                <button 
                  className="add-option-button"
                  onClick={() => setShowChannelModal(true)}
                >
                  Add Channel
                </button>
                <button 
                  className="add-option-button"
                  onClick={handleGetAIRecommendations}
                  disabled={showAIRecommendations}
                >
                  Get AI Recommendations ✨
                </button>
              </div>
            </div>

            {allChannels.length > 0 && (
              <div className="channels-list">
                <div className="channels-header">
                  <div className="select-options">
                    <label className="select-all-option">
                      <input
                        type="checkbox"
                        checked={allChannelsSelected}
                        onChange={(e) => {
                          setAllChannelsSelected(e.target.checked);
                          const newSelection = e.target.checked 
                            ? recommendedChannels.map(c => c.id)
                            : [];
                          setSelectedChannels(newSelection);
                          localStorage.setItem(`channelSelections_${id}`, JSON.stringify(newSelection));
                        }}
                      />
                      <span>Select All Channels</span>
                    </label>
                    <label className="select-all-option">
                      <input
                        type="checkbox"
                        checked={allAIChannelsSelected}
                        onChange={(e) => {
                          setAllAIChannelsSelected(e.target.checked);
                          const aiRecommendedIds = recommendedChannels
                            .filter(c => c.isAIRecommended)
                            .map(c => c.id);
                          const nonAiSelectedIds = selectedChannels
                            .filter(id => !aiRecommendedIds.includes(id));
                          
                          const newSelection = e.target.checked
                            ? [...nonAiSelectedIds, ...aiRecommendedIds]
                            : nonAiSelectedIds;
                          
                          setSelectedChannels(newSelection);
                          localStorage.setItem(`channelSelections_${id}`, JSON.stringify(newSelection));
                        }}
                      />
                      <span>Select All AI Recommended</span>
                    </label>
                  </div>
                  <div className="score-legend">
                    <span className="legend-item">
                      <span className="legend-color notice"></span>
                      Notice
                    </span>
                    <span className="legend-item">
                      <span className="legend-color want"></span>
                      Want/Desire
                    </span>
                    <span className="legend-item">
                      <span className="legend-color seek"></span>
                      Seek More
                    </span>
                    <span className="legend-item">
                      <span className="legend-color try"></span>
                      Try/Buy
                    </span>
                    <span className="legend-item">
                      <span className="legend-color talk"></span>
                      Talk About
                    </span>
                    <span className="legend-item">
                      <span className="legend-color recommend"></span>
                      Recommend
                    </span>
                  </div>
                </div>
                {allChannels.map(channel => (
                  <div key={channel.id} className="channel-item">
                    <div className="channel-header">
                      <div className="channel-name-group">
                        <input
                          type="checkbox"
                          checked={selectedChannels.includes(channel.id)}
                          onChange={() => handleChannelSelect(channel.id)}
                        />
                        <div className="channel-name">
                          {channel.name}
                          {channel.isAIRecommended && (
                            <span className="ai-badge" title="Recommended by AI">
                              <span role="img" aria-label="sparkle">✨</span>
                              AI recommended
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="score-bar">
                        {channel.scores && Object.entries(channel.scores).map(([key, value]) => (
                          <div
                            key={key}
                            className={`score-segment ${key}`}
                            style={{ width: `${value}%` }}
                          />
                        ))}
                      </div>
                      <div className="score-actions">
                        <span className="overall-score">
                          Effectiveness Score: {channel.overallScore}
                        </span>
                        <button
                          className="delete-channel-button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteChannel(channel.id);
                          }}
                          title="Remove channel"
                        >
                          ×
                        </button>
                      </div>
                    </div>
                    {channel.isAIRecommended && (
                      <details className="channel-reasoning">
                        <summary>View AI Reasoning</summary>
                        <p>{channel.reasoning}</p>
                      </details>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {showChannelModal && (
        <AddChannelModal 
          onClose={() => setShowChannelModal(false)}
          existingChannels={allChannels.map(c => c.id)}
          availableChannels={availableChannels}
          onSelect={(channels) => {
            const newChannels = channels.map(id => {
              const channelData = availableChannels.find(c => c.id === id);
              return {
                ...channelData,  // This will include all the scores
                isAIRecommended: false
              };
            });
            
            setAllChannels(prev => [...prev, ...newChannels]);
            setSelectedChannels(prev => [...prev, ...channels]);
            localStorage.setItem(`channelSelections_${id}`, JSON.stringify([...selectedChannels, ...channels]));
            setShowChannelModal(false);
          }}
        />
      )}
    </main>
  );
} 